import { ChangeDetectorRef, Component, OnDestroy, ViewChild, AfterViewInit } from '@angular/core';
import { FormControl, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { debounceTime, catchError, takeUntil } from 'rxjs/operators';
import { forkJoin, of, Subject } from 'rxjs';

import { DatapointFilter } from '../../model/datapoint/filter/datapoint-filter';
import { LayerExposedService } from '../../account/layer-exposed/services/layer-exposed.service';
import { DatapointsFilterService } from '../../dataset/datapoints/datapoints-filter.service';
import { ReportDisplayType } from 'src/app/model/analytics/report-display-type';
import { NotifService } from 'src/app/core/notification/notif.service';
import { DownloadService } from 'src/app/data-access-layer/download/download.service';
import { ChartDataSets } from 'chart.js';
import { ColorUtils } from 'src/app/core/utils/color-utils';
import { StringUtils } from 'src/app/core/utils/string-utils';
import { DownloadReportTableRequest } from 'src/app/model/download/download-report-table-request';
import { DownloadReportItem } from 'src/app/model/download/item/download-report-item';
import { TableColumn, TableColumnType, TableColumnAlignment } from 'src/app/model/upload/table/table-column';
import { TableCell } from 'src/app/model/upload/table/table-cell';
import { TableRow } from 'src/app/model/upload/table/table-row';
import { Constants } from 'src/app/constants';
import { AttachmentUtils } from 'src/app/core/utils/attachment-utils';
import { DownloadDatapointRequest } from 'src/app/model/download/download-datapoint-request';
import { DownloadFileType } from 'src/app/model/download/download-file-type';
import { DownloadReportRequest } from 'src/app/model/download/download-report-request';
import { DownloadReportChartRequest, ValueKey } from 'src/app/model/download/download-report-chart-request';
import { DownloadReportChartValueRequest } from 'src/app/model/download/download-report-chart-value-request';
import { DownloadItemReportType } from 'src/app/model/download/item/download-item-report-type';
import { LayerExposedConstants } from 'src/app/account/layer-exposed/layer-exposed.constants';

@Component({
    selector: 'map-layer-expose-accumulation',
    templateUrl: './layer-expose-accumulation.component.html',
    styleUrls: ['./layer-expose-accumulation.component.scss']
})
export class LayerExposeAccumulationComponent implements OnDestroy, AfterViewInit {
    // Subscription Management
    private destroy$ = new Subject<void>();
    private previousFilterHash: string = '';
    private sortSubscription: any;

    // Route Parameters
    accountId: number;
    datasetId: number;

    // Form and Filter
    filter: DatapointFilter;
    layerExposeForm: UntypedFormGroup;
    exposeLayerEquations: any;
    showError = false;

    // Table Data
    dataSource: MatTableDataSource<any>;
    displayedColumns: string[] = [LayerExposedConstants.COLUMN_NAME.POLICY_NUMBER, LayerExposedConstants.COLUMN_NAME.COUNT];
    mergedData: Map<string, any> = new Map();
    columnTotals: Map<number, number> = new Map();
    @ViewChild(MatSort, { static: false }) sort: MatSort;
    showTable: boolean = false;
    dataIsReady = false;
    
    // Client-side sorting state
    currentSortColumn: string = '';
    currentSortDirection: 'asc' | 'desc' | '' = '';
    private sortConnected: boolean = false;

    // Applied Equations (Chips)
    appliedEquations: Array<{ id: number; name: string; client?: string }> = [];
    showAllChips = false;

    // Loading State
    isRefreshingData = false;

    // Chart Data
    chartDatasets: ChartDataSets[];
    chartLabels: string[];
    chartColors: any[];

    // Report Settings
    reportSubType: ReportDisplayType = ReportDisplayType.TABLE;
    reportName: string;

    constructor(
        private layerExposeService: LayerExposedService,
        private readonly route: ActivatedRoute,
        private datapointsFilterService: DatapointsFilterService,
        private readonly changeDetector: ChangeDetectorRef,
        private readonly downloadService: DownloadService,
        private readonly notifService: NotifService
    ) {
        this.exposeLayerEquations = [];
        this.layerExposeForm = new UntypedFormGroup({});
        this.filter = datapointsFilterService.getActiveFilter();
    }

    ngOnInit() {
        this.accountId = +this.route.snapshot.paramMap.get('accountId');
        this.datasetId = +this.route.snapshot.paramMap.get("datasetId");
        this.layerExposeForm = new UntypedFormGroup({
            equationId: new FormControl('', [Validators.required]),
            groupByClientId: new FormControl(false), // Default to unchecked
        });

        this.layerExposeService.getAppliedLayerExpose(this.accountId)
            .subscribe(equations => {
                this.exposeLayerEquations = equations;
            });

        // Listen to groupByClientId checkbox changes
        this.layerExposeForm.get('groupByClientId')?.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                // Rebuild data source when grouping changes
                if (this.mergedData.size > 0) {
                    this.rebuildDataSource();
                }
            });

        this.datapointsFilterService.onFilterChange()
            .pipe(
                debounceTime(200),
                takeUntil(this.destroy$)
            )
            .subscribe(newFilter => {
                // Only refresh if filter actually changed and we have applied equations
                const newFilterHash = this.getFilterHash(newFilter);
                if (newFilterHash !== this.previousFilterHash && this.appliedEquations.length > 0) {
                    this.filter = newFilter;
                    this.previousFilterHash = newFilterHash;
                    this.reportSubType = ReportDisplayType.TABLE;
                    this.refreshAllAppliedEquations();
                } else {
                    // Just update the filter reference without refetching
                    this.filter = newFilter;
                }
            });
    }

    ngAfterViewInit(): void {
        // MatSort might not be available yet if table hasn't rendered
        // We'll connect it when data is loaded
    }
    
    // Connect MatSort to listen for sort changes (only once)
    private connectSort(): void {
        if (!this.sort || this.sortConnected) {
            return;
        }
        
        this.sortConnected = true;
        this.sortSubscription = this.sort.sortChange.subscribe(() => {
            this.currentSortColumn = this.sort.active;
            this.currentSortDirection = this.sort.direction;
            this.applySorting();
            this.changeDetector.detectChanges();
        });
    }

    ngOnDestroy(): void {
        // Clear selected equation and applied chips when panel is closed
        this.layerExposeForm.get('equationId')?.setValue(null);
        this.appliedEquations = [];
        this.clearData();
        
        // Clean up subscriptions to prevent memory leaks
        this.destroy$.next();
        this.destroy$.complete();
        
        if (this.sortSubscription) {
            this.sortSubscription.unsubscribe();
        }
    }

    get equationIdControl(): UntypedFormControl {
        return this.layerExposeForm.get('equationId') as UntypedFormControl;
    }

    // Generate a hash of the filter to detect actual changes
    private getFilterHash(filter: DatapointFilter): string {
        if (!filter) return '';
        // Create a simple hash based on key filter properties
        // These are the properties that affect layer exposure data
        return JSON.stringify({
            geometryFilter: filter.geometryFilter,
            fields: filter.fields,
            groups: filter.groups,
            datasetID: filter.datasetID
        });
    }

    fetchLayerExposeData(): void {
        this.dataIsReady = true;
        if (!this.equationIdControl.valid) {
            this.showError = true;
            this.dataIsReady = false;
            return;
        }
        this.filter = this.datapointsFilterService.getActiveFilter();
        const selectedEquationId = this.layerExposeForm.get('equationId')?.value;
        const selectedEquation = this.exposeLayerEquations?.find((e:any)=> e.id === selectedEquationId);
        
        if (!selectedEquation) {
            return;
        }

        // Show loader for single equation fetch
        this.isRefreshingData = false;//true;

        this.layerExposeService.getLayerSummaryResponse(this.filter, this.accountId, selectedEquationId)
            .subscribe({
                next: (data) => {
                    this.reportSubType = ReportDisplayType.TABLE;
                    this.showTable = true;
                    
                    // Merge new data with existing data
                    this.mergeEquationData(selectedEquationId, selectedEquation.name, data);
                    
                    // After successful apply, add current selection to chips list if not already present
                    this.addAppliedEquationIfNeeded();
                     // Hide loader
                     this.isRefreshingData = false;
                    this.changeDetector.detectChanges();
                    
                   
                },
                error: (error) => {
                    console.error('Error fetching layer expose data:', error);
                    this.notifService.error(LayerExposedConstants.ERROR_MESSAGE.LOAD_EQUATION_FAILED);
                    
                    // Hide loader on error
                    this.isRefreshingData = false;
                }
            });
    }

    clearData() {
        this.showTable = false;
        this.equationIdControl.setValue(null);
        this.showError = false;
        this.appliedEquations = [];
        // Clear all merged data and reset columns
        this.mergedData.clear();
        this.columnTotals.clear();
        this.displayedColumns = [LayerExposedConstants.COLUMN_NAME.POLICY_NUMBER, LayerExposedConstants.COLUMN_NAME.COUNT];
        this.dataSource = null;
        
        // Reset sorting state to allow sorting to work when data is fetched again
        this.currentSortColumn = '';
        this.currentSortDirection = '';
        this.sortConnected = false;
        
        // Unsubscribe from sort subscription if it exists
        if (this.sortSubscription) {
            this.sortSubscription.unsubscribe();
            this.sortSubscription = null;
        }
    }

    // Removes the selected equation via the chip's cross icon
    onRemoveEquation(): void {
        // Clear selection and any displayed data
        this.clearData();
    }

    // Add the currently selected equation to appliedEquations if not present
    private addAppliedEquationIfNeeded(): void {
        const selectedId: number = this.layerExposeForm.get('equationId')?.value;
        if (!selectedId) { return; }
        const eq = this.exposeLayerEquations?.find((e:any)=> e.id === selectedId);
        if (!eq) { return; }
        const exists = this.appliedEquations.some(e => e.id === eq.id);
        if (!exists) {
            this.appliedEquations.push({ id: eq.id, name: eq.name, client: eq.client });
        }
        return;
    }

    // Remove a specific applied equation chip
    removeAppliedEquation(eq: {id:number; name:string}): void {
        this.appliedEquations = this.appliedEquations.filter(e => e.id !== eq.id);
        
        // Remove the column from displayedColumns
        const columnKey = `equation_${eq.id}`;
        this.displayedColumns = this.displayedColumns.filter(col => col !== columnKey);
        
        // Remove data for this equation from merged data
        this.mergedData.forEach((row) => {
            delete row[columnKey];
        });
        
        // Remove total for this equation
        this.columnTotals.delete(eq.id);
        
        // Rebuild data source
        this.rebuildDataSource();
        
        // If the removed chip is the currently selected one, clear selection
        if (this.layerExposeForm.get('equationId')?.value === eq.id) {
            this.equationIdControl.setValue(null);
        }
        this.reportSubType = ReportDisplayType.TABLE;
        // If no equations left, hide table and reset sorting state
        if (this.appliedEquations.length === 0) {
            this.showTable = false;
            this.currentSortColumn = '';
            this.currentSortDirection = '';
            this.sortConnected = false;
            if (this.sortSubscription) {
                this.sortSubscription.unsubscribe();
                this.sortSubscription = null;
            }
        }
    }

    // trackBy for chip list
    trackByEqId(index: number, item: {id:number; name:string}) { return item.id; }

    // Returns the chips to render depending on collapsed/expanded state
    get visibleAppliedEquations(): Array<{id:number; name:string}> {
        return this.showAllChips ? this.appliedEquations : this.appliedEquations.slice(0, 8);
    }

    toggleChipsView(): void {
        this.showAllChips = !this.showAllChips;
    }

    clearAllApplied(): void {
        this.appliedEquations = [];
        this.clearData();
    }
    
    // Re-fetch data for all applied equations (used when filter changes)
    private refreshAllAppliedEquations(): void {
        if (this.appliedEquations.length === 0) {
            return;
        }
        
        // Show loader
        this.isRefreshingData = false//true;
        this.changeDetector.detectChanges();
        // Clear existing merged data
        this.mergedData.clear();
        this.columnTotals.clear();
        
        // Create an array of observables with individual error handling
        // Each request catches its own error and returns null on failure
        const requests = this.appliedEquations.map(equation => 
            this.layerExposeService.getLayerSummaryResponse(this.filter, this.accountId, equation.id)
                .pipe(
                    catchError(error => {
                        console.error(`Failed to load data for equation "${equation.name}":`, error);
                        // Return null for failed requests so forkJoin continues
                        return of(null);
                    })
                )
        );
        
        // Execute all API calls in parallel using forkJoin
        // Now forkJoin will complete even if some requests fail
        forkJoin(requests).subscribe({
            next: (results) => {
                let successCount = 0;
                let failureCount = 0;
                
                // Process all results
                results.forEach((data, index) => {
                    const equation = this.appliedEquations[index];
                    
                    if (data !== null) {
                        // Success - merge the data
                        this.mergeEquationData(equation.id, equation.name, data);
                        successCount++;
                    } else {
                        // Failure - skip this equation
                        failureCount++;
                    }
                });
                
                // Update UI after all data is merged
                this.changeDetector.detectChanges();
                
                // Hide loader
                this.isRefreshingData = false;
                
                // Show appropriate notification based on results
                if (failureCount > 0 && successCount > 0) {
                    // Partial success
                    this.notifService.error(
                        `Failed to load ${failureCount} of ${this.appliedEquations.length} equations. Showing partial data.`
                    );
                } else if (failureCount > 0 && successCount === 0) {
                    // Complete failure
                    this.notifService.error(LayerExposedConstants.ERROR_MESSAGE.LOAD_ALL_EQUATIONS_FAILED);
                }
                // If all successful (failureCount === 0), no notification needed
            },
            error: (error) => {
                // This should rarely happen now since we handle errors per request
                console.error('Unexpected error in refreshAllAppliedEquations:', error);
                this.notifService.error(LayerExposedConstants.ERROR_MESSAGE.UNEXPECTED_ERROR);
                
                // Hide loader on error
                this.isRefreshingData = false;
            }
        });
    }
    
    // Merge new equation data with existing data by policy
    private mergeEquationData(equationId: number, equationName: string, data: any): void {
        const columnKey = `equation_${equationId}`;

        // Add column if not already present
        if (!this.displayedColumns.includes(columnKey)) {
            // Insert before 'count' column
            const countIndex = this.displayedColumns.indexOf('count');
            this.displayedColumns.splice(countIndex, 0, columnKey);
        }

        // Calculate total for this equation
        let total = 0;

        // First pass: aggregate raw response rows by policy so repeated
        // policynumber values are summed instead of overwritten.
        const aggregatedByKey = new Map<string, any>();

        data.exposureResponses?.forEach((response: any) => {
            const policynumber = response.policynumber;

            if (!aggregatedByKey.has(policynumber)) {
                aggregatedByKey.set(policynumber, {
                    policynumber: policynumber,
                    clientName: response.clientName || 'Unknown',
                    minimumDeductible: response.minimumDeductible,
                    maximumDeductible: response.maximumDeductible,
                    baseValue: response.baseValue,
                    countOfLocations: 0,
                    [columnKey]: 0
                });
            }

            const row = aggregatedByKey.get(policynumber);
            row[columnKey] += response.exposedValue || 0;
            row.countOfLocations += response.countOfLocations || 0;

            // Keep the last real client name and deductibles if available
            if (response.clientName) {
                row.clientName = response.clientName;
            }
            if (response.minimumDeductible !== undefined) {
                row.minimumDeductible = response.minimumDeductible;
            }
            if (response.maximumDeductible !== undefined) {
                row.maximumDeductible = response.maximumDeductible;
            }
            if (response.baseValue !== undefined) {
                row.baseValue = response.baseValue;
            }

            total += response.exposedValue || 0;
        });

        // Second pass: merge aggregated rows into global mergedData by policy
        aggregatedByKey.forEach((aggregatedRow, policynumber) => {
            if (!this.mergedData.has(policynumber)) {
                this.mergedData.set(policynumber, aggregatedRow);
            } else {
                const existingRow = this.mergedData.get(policynumber);
                existingRow[columnKey] = aggregatedRow[columnKey];
                existingRow.countOfLocations = aggregatedRow.countOfLocations;
                // Keep the last real client name and deductibles if available
                if (aggregatedRow.clientName && aggregatedRow.clientName !== 'Unknown') {
                    existingRow.clientName = aggregatedRow.clientName;
                }
                if (aggregatedRow.minimumDeductible !== undefined) {
                    existingRow.minimumDeductible = aggregatedRow.minimumDeductible;
                }
                if (aggregatedRow.maximumDeductible !== undefined) {
                    existingRow.maximumDeductible = aggregatedRow.maximumDeductible;
                }
                if (aggregatedRow.baseValue !== undefined) {
                    existingRow.baseValue = aggregatedRow.baseValue;
                }
            }
        });

        // Store total for this equation
        this.columnTotals.set(equationId, total);

        // Rebuild data source
        this.rebuildDataSource();
    }
    
    // Apply sorting to current data (optimized for large datasets)
    private applySorting(): void {
        const dataArray = Array.from(this.mergedData.values());
        
        // If no sorting or direction is empty (third click clears sort), use original order
        if (!this.currentSortColumn || !this.currentSortDirection) {
            this.dataSource = new MatTableDataSource<any>(dataArray);
            // Connect MatSort to dataSource
            if (this.sort) {
                this.dataSource.sort = this.sort;
            }
            setTimeout(() => this.connectSort(), 0);
            return;
        }
        
        // Perform efficient sorting
        const sortedData = dataArray.sort((a, b) => {
            let aValue: any;
            let bValue: any;
            
            // Get values based on column
            if (this.currentSortColumn === 'count') {
                aValue = a.countOfLocations || 0;
                bValue = b.countOfLocations || 0;
            } else if (this.currentSortColumn === 'policynumber') {
                aValue = a.policynumber || '';
                bValue = b.policynumber || '';
            } else {
                // Equation columns
                aValue = a[this.currentSortColumn] || 0;
                bValue = b[this.currentSortColumn] || 0;
            }
            
            // Compare values
            let comparison = 0;
            if (typeof aValue === 'string') {
                comparison = aValue.localeCompare(bValue);
            } else {
                comparison = aValue - bValue;
            }
            
            // Apply direction
            return this.currentSortDirection === 'asc' ? comparison : -comparison;
        });
        
        // Update dataSource
        this.dataSource = new MatTableDataSource<any>(sortedData);
        // Connect MatSort to dataSource
        if (this.sort) {
            this.dataSource.sort = this.sort;
        }
        setTimeout(() => this.connectSort(), 0);
    }
    
    // Rebuild MatTableDataSource from merged data
    private rebuildDataSource(): void {
        const groupByClientId = this.layerExposeForm.get('groupByClientId')?.value;
        
        if (groupByClientId) {
            this.buildGroupedDataSource();
        } else {
            // Simply call applySorting which handles both sorted and unsorted cases
            this.applySorting();
        }
        
        this.showTable = true;
        this.dataIsReady = true;
    }
    
    // Build data source with Client Name grouping
    private buildGroupedDataSource(): void {
        const dataArray = Array.from(this.mergedData.values());
        
        // Group data by client name
        const groupedData: any[] = [];
        const clientGroups = new Map<string, any[]>();
        
        // First, group all policies by their client name
        dataArray.forEach(row => {
            const clientName = row.clientName || 'Unknown';
            if (!clientGroups.has(clientName)) {
                clientGroups.set(clientName, []);
            }
            clientGroups.get(clientName)!.push(row);
        });
        
        // Now create the grouped structure with headers and totals
        clientGroups.forEach((policies, clientName) => {
            // Calculate totals for this client group
            let totalLocations = 0;
            const equationTotals: any = {};
            
            policies.forEach(policy => {
                totalLocations += policy.countOfLocations || 0;
                
                // Sum up each equation column
                this.displayedColumns.forEach(col => {
                    if (col.startsWith('equation_')) {
                        if (!equationTotals[col]) {
                            equationTotals[col] = 0;
                        }
                        equationTotals[col] += policy[col] || 0;
                    }
                });
            });
            
            // Add group header row with totals
            groupedData.push({
                isGroupHeader: true,
                clientName: clientName,
                policynumber: clientName,
                countOfLocations: totalLocations,
                policyCount: policies.length,
                ...equationTotals
            });
            
            // Add all policies in this group
            policies.forEach(policy => {
                groupedData.push({
                    ...policy,
                    isGroupHeader: false,
                    isGroupChild: true
                });
            });
        });
        
        this.dataSource = new MatTableDataSource<any>(groupedData);
        
        // Connect MatSort to dataSource
        if (this.sort) {
            this.dataSource.sort = this.sort;
        }
        setTimeout(() => this.connectSort(), 0);
    }
    
    // Get equation name by column key (truncate if > 20 chars)
    getEquationNameByColumn(columnKey: string): string {
        if (columnKey === 'policynumber') return 'Policy Number';
        if (columnKey === 'count') return 'Count';
        
        const equationId = parseInt(columnKey.replace('equation_', ''));
        const equation = this.appliedEquations.find(eq => eq.id === equationId);
        const name = equation ? equation.name : columnKey;
        
        // Truncate if longer than 20 characters
        return name.length > 20 ? name.substring(0, 20) + '...' : name;
    }
    
    // Get total for a specific equation column
    getTotalForColumn(columnKey: string): number {
        if (columnKey === 'count') {
            // Sum all countOfLocations
            let total = 0;
            this.mergedData.forEach(row => {
                total += row.countOfLocations || 0;
            });
            return total;
        }
        
        const equationId = parseInt(columnKey.replace('equation_', ''));
        return this.columnTotals.get(equationId) || 0;
    }
    
    // Check if column is a dynamic equation column
    isEquationColumn(columnKey: string): boolean {
        return columnKey.startsWith('equation_');
    }
    
    // Get grand total of all equation columns
    getGrandTotal(): number {
        let grandTotal = 0;
        this.columnTotals.forEach((total) => {
            grandTotal += total;
        });
        return grandTotal;
    }
    
    close() {
        // Clear equation selection and chips when closing the table
        this.layerExposeForm.get('equationId')?.setValue(null);
        this.appliedEquations = [];
        this.clearData();
        this.dataIsReady = false;
        this.reportSubType = ReportDisplayType.TABLE;
    }
    
    get ReportDisplayType() {
        return ReportDisplayType;
    }

    // ============================================
    // CHART METHODS
    // ============================================

    chartOptions: any = {
        responsive: true,
        tooltips: {
            callbacks: {
                label: function (tooltipItem, data) {
                    const value =
                        data.datasets[tooltipItem.datasetIndex].data[
                            tooltipItem.indexs
                        ];
                    const name = data.labels[tooltipItem.index];
                    const roundedUpValue = value;
                    let generatedTooltip = `${name}:${StringUtils.numberWithCommas(
                        roundedUpValue
                    )}`;

                    return generatedTooltip;
                },
            },
        },
    };

    private initializeChartData() {
        this.chartDatasets = [];
        this.chartLabels = [];
        this.chartColors = [];
    }

    private populateChartData() {
        if (!this.dataSource || this.dataSource.data.length === 0) {
            return;
        }
        this.initializeChartData();

        // Get all equation columns (exclude accountId and count)
        const equationColumns = this.displayedColumns.filter(col => this.isEquationColumn(col));
        
        if (equationColumns.length === 0) {
            return;
        }

        // Account IDs will be the X-axis labels
        const policyNumbers: string[] = [];
        this.dataSource.data.forEach((row: any) => {
            policyNumbers.push(row.policynumber);
        });
        this.chartLabels = policyNumbers;

        // Create a dataset for each equation
        let chartDatasets: ChartDataSets[] = [];
        
        equationColumns.forEach((columnKey: string, index: number) => {
            const equationName = this.getEquationNameByColumn(columnKey);
            const color = ColorUtils.generateRandomHexColor();
            
            // Create dataset for this equation
            const dataset: ChartDataSets = {
                label: equationName,
                data: [],
                backgroundColor: color,
                borderColor: color,
                borderWidth: 1
            };
            
            // Populate data for each account
            this.dataSource.data.forEach((row: any) => {
                dataset.data.push(row[columnKey] || 0);
            });
            
            chartDatasets.push(dataset);
        });

        this.chartDatasets = chartDatasets;
    }

    setDisplayType(reportSubType: ReportDisplayType) {
        if (this.accountId > 0) {
            this.reportSubType = reportSubType;
        }
        this.chartOptions.scales =
            reportSubType === ReportDisplayType.BAR_CHART
                ? {
                      yAxes: [
                          {
                              ticks: {
                                  callback: (value) => {
                                      return StringUtils.numberWithCommas(
                                          value
                                      );
                                  },
                              },
                          },
                      ],
                  }
                : {};

        this.chartOptions.tooltips = {
            callbacks: {
                label: (tooltipItem, data) => {
                    const value =
                        data.datasets[tooltipItem.datasetIndex].data[
                            tooltipItem.index
                        ];
                    const name = data.labels[tooltipItem.index];
                    const roundedUpValue = value;
                    let generatedTooltip = `${name}:${StringUtils.numberWithCommas(
                        roundedUpValue
                    )}`;

                    return generatedTooltip;
                },
            },
        };

        this.populateChartData();

        // When switching back to table view, reconnect sorting
        if (reportSubType === ReportDisplayType.TABLE && this.dataSource) {
            // Reset sorting connection flag to allow reconnection
            this.sortConnected = false;
            
            // Trigger change detection and reconnect sort after view updates
            this.changeDetector.detectChanges();
            setTimeout(() => {
                if (this.sort && this.dataSource) {
                    this.dataSource.sort = this.sort;
                    this.connectSort();
                }
            }, 0);
        }
    }

    // ============================================
    // DOWNLOAD/EXPORT METHODS
    // ============================================

    getTableReportHeader(): TableColumn[] {
        let columns: TableColumn[] = [];

        // Add Account ID column
        columns.push({
            id: 'policynumber',
            name: 'Policy Number',
            type: TableColumnType.TEXT,
            horizontalAlignment: TableColumnAlignment.LEFT,
        });

        // Add dynamic equation columns
        this.displayedColumns.forEach((columnKey) => {
            if (this.isEquationColumn(columnKey)) {
                columns.push({
                    id: columnKey,
                    name: this.getEquationNameByColumn(columnKey),
                    type: TableColumnType.TEXT,
                    horizontalAlignment: TableColumnAlignment.LEFT,
                });
            }
        });

        // Add Count column
        columns.push({
            id: 'count',
            name: 'Count',
            type: TableColumnType.TEXT,
            horizontalAlignment: TableColumnAlignment.LEFT,
        });

        return columns;
    }

    // Get sorted data from dataSource (for downloads)
    private getSortedData(): any[] {
        if (!this.dataSource) {
            return [];
        }
        
        // Return data in current display order (already sorted by applySorting)
        return this.dataSource.data;
    }

    getTableReportRows(): TableRow[] {
        let rows: TableRow[] = [];
        const sortedData: any[] = this.getSortedData();
        
        sortedData.forEach((row: any) => {
            let cells: TableCell[] = [];
            const isBold = row.isGroupHeader; // Client group headers should be bold
            
            // Add Policy Number / Client Name cell with indentation for child policies
            let policyNumberValue = row.policynumber || "-";
            if (row.isGroupChild && !row.isGroupHeader) {
                // Add indentation for policy rows under client groups
                policyNumberValue = "  " + policyNumberValue;
            }
            cells.push({ 
                id: "policynumber", 
                value: policyNumberValue,
                bold: isBold
            });
            
            // Add dynamic equation cells
            this.displayedColumns.forEach((columnKey) => {
                if (this.isEquationColumn(columnKey)) {
                    cells.push({
                        id: columnKey,
                        value: row[columnKey] !== null && row[columnKey] !== undefined ? row[columnKey] : "-",
                        bold: isBold
                    });
                }
            });
            
            // Add Count cell - show just the number for both group headers and regular rows
            cells.push({
                id: "count",
                value: row.countOfLocations !== null && row.countOfLocations !== undefined ? row.countOfLocations : "-",
                bold: isBold
            });
            
            rows.push({
                cells: cells,
            });
        });
        return rows;
    }

    getTableReportFooter(): TableRow {
        let cells: TableCell[] = [];

        // Add "Total" label for Account ID
        cells.push({ id: "policynumber", value: "Total" });
        
        // Add totals for each equation column
        this.displayedColumns.forEach((columnKey) => {
            if (this.isEquationColumn(columnKey)) {
                const total = this.getTotalForColumn(columnKey);
                cells.push({ 
                    id: this.getEquationNameByColumn(columnKey), 
                    value: total 
                });
            }
        });
        
        // Add total for Count column
        const totalOfCounts = this.getTotalForColumn('count');
        cells.push({ id: "Count", value: totalOfCounts });

        return { cells: cells };
    }

    getDisplayType(): ReportDisplayType {
        return this.reportSubType;
    }

    getReportDownloadRequest(): DownloadReportItem {
        if (this.getDisplayType() === ReportDisplayType.TABLE) {
            return this.getTableReportDownloadRequest();
        } else if (
            this.getDisplayType() === ReportDisplayType.BAR_CHART ||
            this.getDisplayType() === ReportDisplayType.PIE_CHART
        ) {
            return this.getChartReportDownloadRequest();
        }
    }

    getChartReportDownloadRequest(): DownloadReportItem {
        // Get all equation names for the chart title
        const equationNames = this.appliedEquations.map(eq => eq.name).join(', ');
        let breakdownFieldsNames: string[] = ["Policy Number"];

        let request: DownloadReportChartRequest = {
            title: this.reportName || equationNames + " Chart",
            valueKey: ValueKey.EXPOSE,
            type: undefined,
            columns: {
                value: "Policy Number",
                categories: breakdownFieldsNames,
            },
            values: [],
        };

        // For multi-equation charts, we'll use the first equation column for download
        // Or sum all equations - depending on requirements
        const equationColumns = this.displayedColumns.filter(col => this.isEquationColumn(col));
        const firstEquationColumn = equationColumns[0];

        // Use sorted data for download
        const sortedData = this.getSortedData();
        sortedData.forEach((item) => {
            // Use first equation value or sum of all equations
            const value = firstEquationColumn ? (item[firstEquationColumn] || 0) : 0;
            
            let chartValueRequest: DownloadReportChartValueRequest = {
                categories: [item.policynumber],
                colors: ["#36A2EB"],
                count: value,
                value: value,
            };
            request.values.push(chartValueRequest);
        });

        if (this.reportSubType === ReportDisplayType.PIE_CHART) {
            request.type = DownloadItemReportType.AGGREGATE_PIE_CHART;
        } else if (this.reportSubType === ReportDisplayType.BAR_CHART) {
            request.type = DownloadItemReportType.AGGREGATE_BAR_CHART;
        }

        return request;
    }

    getTableReportDownloadRequest(): DownloadReportItem {
        if (this.dataIsReady && this.dataSource && this.dataSource.filteredData.length > 0) {
            let reportHeader = this.getTableReportHeader();
            let reportRows = this.getTableReportRows();
            let reportFooter = this.getTableReportFooter();
            
            // Generate title with equation names
            let title = this.reportName;
            if (!title) {
                const equationNames = this.appliedEquations.map(eq => eq.name).join(', ');
                title = equationNames || "Layer Exposed";
            }

            return new DownloadReportTableRequest(
                title,
                reportHeader,
                reportRows,
                reportFooter
            );
        } else {
            return null;
        }
    }

    downloadReports() {
        // Validate that we have data to download
        if (!this.dataSource || !this.dataSource.filteredData || this.dataSource.filteredData.length === 0) {
            this.notifService.error(LayerExposedConstants.ERROR_MESSAGE.NO_DATA_TO_DOWNLOAD);
            return;
        }

        // Validate that we have at least one equation selected
        if (this.appliedEquations.length === 0) {
            this.notifService.error(LayerExposedConstants.ERROR_MESSAGE.NO_EQUATION_SELECTED);
            return;
        }

        let downloadItems: DownloadReportItem[] = [];

        let reportDownloadRequest: DownloadReportItem =
            this.getReportDownloadRequest();
        if (reportDownloadRequest) {
            downloadItems.push(reportDownloadRequest);
        }

        if (downloadItems.length === 0) {
            this.notifService.error(LayerExposedConstants.ERROR_MESSAGE.DOWNLOAD_REQUEST_FAILED);
            return;
        }

        let reportRequest: DownloadReportRequest = {
            items: downloadItems,
        };

        let downloadRequest: DownloadDatapointRequest = {
            reportRequest: reportRequest,
            filter: this.datapointsFilterService.getActiveFilter(),
            timezone: Constants.DEFAULT_TIMEZONE,
            dateFormat: Constants.DEFAULT_DATE_FORMAT,
            outputFileType: DownloadFileType.XLSX,
        };

        this.downloadReportFile(downloadRequest);
    }

    private downloadReportFile(downloadRequest: DownloadDatapointRequest) {
        this.downloadService
            .downloadDatapoints(
                this.datasetId.toString(),
                downloadRequest,
                LayerExposedConstants.DOWNLOAD.FILE_NAME
            )
            .subscribe(
                (response) => {
                    AttachmentUtils.downloadFileWithName(
                        response,
                        LayerExposedConstants.DOWNLOAD.FILE_NAME
                    );
                },
                (error) =>
                    this.notifService.error(
                        LayerExposedConstants.ERROR_MESSAGE.DOWNLOAD_FAILED
                    )
            );
    }

    /**
     * Determines whether to show the "Group by Client ID" checkbox
     * Show only if ALL applied equations have a client value selected
     */
    shouldShowGroupByClientId(): boolean {
        if (!this.appliedEquations || this.appliedEquations.length === 0) {
            return false;
        }
        
        // Check if ALL applied equations have a client value (not empty/null/undefined)
        return this.appliedEquations.every(eq => eq.client && eq.client.trim() !== '');
    }

    /**
     * Get the selected equation name for display in mat-select-trigger
     * Returns only the equation name without the "w/ ded" indicator
     */
    getSelectedEquationName(): string {
        const selectedId = this.layerExposeForm.get('equationId')?.value;
        if (!selectedId) {
            return '';
        }
        const selectedEquation = this.exposeLayerEquations?.find((eq: any) => eq.id === selectedId);
        return selectedEquation ? selectedEquation.name : '';
    }
}